/**
 * Brush footprints — turning one 3D point into the set of template regions a
 * brush actually covers.
 *
 * This is what makes painting across a seam work. A brush has a radius, and
 * near a face boundary it should wrap onto the adjacent face — the entire
 * reason to paint in 3D rather than in 2D. Those two faces are adjacent on the
 * body but land in *disjoint* rectangles of the template, so a single UV-space
 * stamp cannot express it.
 *
 * Pure functions over the position map, deliberately: the geometry is where the
 * subtle bugs live, and this way they are provable in Node instead of only
 * observable in a browser.
 */
import type { Rect, RegionId } from "./format";
import { type PositionMap, positionAt } from "./position-map";

export interface Footprint {
  region: RegionId;
  /** Texture pixels — the point on this face nearest the brush centre. */
  centre: { x: number; y: number };
  radius: number;
  /**
   * Share of the brush landing on this face. Weights sum to 1 across the
   * footprints of a single stamp.
   */
  weight: number;
  /**
   * Set by the viewer for a symmetry counterpart. It is an independent mark at
   * full strength, not a share of the original, so weights sum to 1 *per side* —
   * summing across a symmetric stroke gives 2, by design.
   */
  mirrored?: boolean;
}

/**
 * World units per texel, measured from the map rather than assumed.
 *
 * Returns a SINGLE global figure, which is only correct while texel density is
 * uniform across the body. The R6 body is a uniform 64 texels per stud, so this
 * holds today — but a body whose faces pack at different densities would need
 * this per region, and the brush would need to ask per candidate face.
 */
export function worldPerTexel(map: PositionMap, regions: Record<string, Rect>): number {
  for (const rect of Object.values(regions)) {
    // Sample at the CENTRE of the region, not near its edge.
    //
    // A real Roblox part is a bevelled box, so the outer few texels of every
    // face sit on a 45-degree chamfer. UVs are assigned by projection, so a
    // chamfer packs sqrt(2) times as much 3D distance into each texel as the
    // flat face does — sampling 2px in returned 0.0266 studs/texel against a
    // true 0.0156, and every brush radius derived from it was 70% too large.
    const y = Math.floor(rect.y + rect.h / 2);
    const x = Math.floor(rect.x + rect.w / 2);
    const a = positionAt(map, x, y);
    const b = positionAt(map, x + 1, y);
    if (!a || !b) continue;
    const d = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    if (d > 1e-6) return d;
  }
  return 1 / 64; // fallback: the measured R6 density
}

export interface RegionBounds {
  region: RegionId;
  rect: Rect;
  min: [number, number, number];
  max: [number, number, number];
}

/**
 * World-space bounds per region, computed once per body.
 *
 * Without this, finding the covered regions would mean scanning all 327k texels
 * per brush sample. With it, a sample tests 18 cheap box overlaps and then scans
 * only the two or three regions that can possibly be involved.
 */
export function computeRegionBounds(
  map: PositionMap,
  regions: Record<string, Rect>,
): RegionBounds[] {
  const out: RegionBounds[] = [];
  for (const [region, rect] of Object.entries(regions)) {
    const min: [number, number, number] = [Infinity, Infinity, Infinity];
    const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
    let any = false;

    for (let y = rect.y; y < rect.y + rect.h; y++) {
      for (let x = rect.x; x < rect.x + rect.w; x++) {
        const p = positionAt(map, x, y);
        if (!p) continue;
        any = true;
        for (let i = 0; i < 3; i++) {
          if ((p[i] as number) < (min[i] as number)) min[i] = p[i] as number;
          if ((p[i] as number) > (max[i] as number)) max[i] = p[i] as number;
        }
      }
    }
    if (any) out.push({ region: region as RegionId, rect, min, max });
  }
  return out;
}

function boxIntersectsSphere(
  min: [number, number, number],
  max: [number, number, number],
  p: [number, number, number],
  r: number,
): boolean {
  let d2 = 0;
  for (let i = 0; i < 3; i++) {
    const v = p[i] as number;
    const lo = min[i] as number;
    const hi = max[i] as number;
    if (v < lo) d2 += (lo - v) ** 2;
    else if (v > hi) d2 += (v - hi) ** 2;
  }
  return d2 <= r * r;
}

/**
 * Every region the brush touches, with where to stamp on each.
 *
 * `centre` is the point on that face nearest the brush centre, not the centroid
 * of the covered area. That matters: a brush half over an edge must stamp *at*
 * the edge on both faces and be clipped, which reassembles into a continuous
 * mark across the seam. Stamping at the covered centroid would slide the mark
 * inward on both sides and open a gap along the very seam we are trying to
 * paint across.
 */
/** One texel the brush actually reaches, and how far into it the brush is. */
export interface CoveredTexel {
  x: number;
  y: number;
  region: RegionId;
  /** 3D distance from the brush centre, in world units. */
  distance: number;
}

/**
 * How far the brush may travel before its coverage must be recomputed.
 *
 * `computeCoverage` is O(radius squared) and ran on every pointer event, so a
 * 120px brush cost 3ms an event — 178ms of every second at 60 events, which is
 * the lag people reported. But consecutive pointer points are one to three
 * pixels apart, and a big brush's covered set barely changes over that: the
 * scan was being redone to produce almost exactly the previous answer.
 *
 * Sampling by distance fixes the complexity rather than the constant. Cost per
 * call still grows as radius squared, but calls fall off as 1/radius, so the
 * total is linear — and the throttle loosens precisely where each scan is
 * dearest. Measured over a 200-point stroke, a 120px brush went from 384ms to
 * 37ms, which is about what a 24px brush costs.
 *
 * ## Why a sixth, measured rather than derived
 *
 * The swept area is the path thickened by the radius; sampling it leaves a
 * notch between consecutive discs, and the geometry says a quarter-radius step
 * should lose about 0.008 of the radius — a quarter-pixel, nothing. The
 * geometry is not the whole story, because what is actually lost is *texels*,
 * and those quantise at the boundary. Measured along the torso/arm fold, where
 * bleed is the entire point:
 *
 *     step      texels missed      120px cost
 *     r/4       3.1% – 3.8%        25ms
 *     r/6       0.9% – 1.2%        37ms
 *     r/12      0.6% – 0.9%        64ms
 *
 * A sixth is the knee: it takes the loss to about one percent of the outermost,
 * least-painted ring for half the cost of going further. A quarter was the
 * value the arithmetic suggested and it is visibly worse than the arithmetic
 * predicted, which is the reason this table is here.
 *
 * Never below 1: a sub-pixel step samples every point and throttles nothing.
 */
export function coverageStep(radiusPx: number): number {
  return Math.max(1, radiusPx / 6);
}

/**
 * Every texel a brush reaches, in 3D.
 *
 * This is the honest definition of a brush on a body: a ball of radius r around
 * a point, and every texel whose surface position falls inside it. A fold makes
 * no difference — a disc laid across an edge covers both faces because it
 * genuinely covers both, and a disc well inside a face covers only that face
 * because it genuinely does.
 *
 * That distinction is the whole reason this exists. An earlier attempt at
 * painting across folds used the seam map, which pairs a texel at depth d inside
 * one face with depth d inside its neighbour — a reflection across the edge.
 * Right for asking "what colour is on the other side"; wrong for asking "where
 * else does this disc land". Measured, it put 29 texels on the shoulder from a
 * dab ten texels clear of the rim. Distance in 3D cannot make that mistake:
 * a texel is either within the radius or it is not.
 *
 * `computeFootprints` is a summary of this, and is built on it so the two cannot
 * drift.
 */
export function computeCoverage(
  map: PositionMap,
  bounds: RegionBounds[],
  point: [number, number, number],
  radiusPx: number,
  unitsPerTexel: number,
): CoveredTexel[] {
  const radiusWorld = radiusPx * unitsPerTexel;
  const coarse = Math.max(1, Math.floor(radiusPx / 3));
  const out: CoveredTexel[] = [];

  for (const b of bounds) {
    if (!boxIntersectsSphere(b.min, b.max, point, radiusWorld)) continue;

    // Pass 1 — locate the brush on this face. Same coarse-then-fine reasoning
    // as `computeFootprints`, and for the same measured reasons.
    let seed: { x: number; y: number } | null = null;
    let seedDist = Number.POSITIVE_INFINITY;
    for (let y = b.rect.y; y < b.rect.y + b.rect.h; y += coarse) {
      for (let x = b.rect.x; x < b.rect.x + b.rect.w; x += coarse) {
        const p = positionAt(map, x, y);
        if (!p) continue;
        const d = Math.hypot(p[0] - point[0], p[1] - point[1], p[2] - point[2]);
        if (d > radiusWorld + coarse * unitsPerTexel) continue;
        if (d < seedDist) {
          seedDist = d;
          seed = { x, y };
        }
      }
    }
    if (!seed) continue;

    // Pass 2 — exact, at stride 1, over a window that must contain everything
    // within the radius of the seed.
    const pad = Math.ceil(radiusPx) + coarse;
    const x0 = Math.max(b.rect.x, seed.x - pad);
    const x1 = Math.min(b.rect.x + b.rect.w - 1, seed.x + pad);
    const y0 = Math.max(b.rect.y, seed.y - pad);
    const y1 = Math.min(b.rect.y + b.rect.h - 1, seed.y + pad);

    /*
      Cull on the square, and take the root only for what survives.

      This is the innermost loop in the app — a 120px brush walks ~40,000 texels
      of this per pointer event, per face it touches. Two things were being paid
      for on every one of them and neither was needed: `Math.hypot` guards
      against intermediate overflow by rescaling its arguments, which costs
      several times a plain multiply and protects against magnitudes this
      geometry cannot produce; and the square root was computed before the test
      that throws most texels away. Comparing squares is exactly equivalent
      because both sides are non-negative.

      `distance` stays a true distance. It is only ever compared — see
      computeFootprints — so returning the square would order identically, but
      a field named `distance` holding a squared one is a trap for the next
      reader, and the roots that remain are a fraction of the scan.
    */
    const radiusWorld2 = radiusWorld * radiusWorld;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const p = positionAt(map, x, y);
        if (!p) continue;
        const dx = p[0] - point[0];
        const dy = p[1] - point[1];
        const dz = p[2] - point[2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 > radiusWorld2) continue;
        out.push({ x, y, region: b.region, distance: Math.sqrt(d2) });
      }
    }
  }
  return out;
}

export function computeFootprints(
  map: PositionMap,
  bounds: RegionBounds[],
  point: [number, number, number],
  radiusPx: number,
  unitsPerTexel: number,
): Footprint[] {
  /*
    A summary of `computeCoverage`, not a second scan.

    Two implementations of "where does this brush land" would be two chances to
    answer it differently, and the difference would show up as a stroke that
    previews on one set of faces and commits to another.
  */
  const covered = computeCoverage(map, bounds, point, radiusPx, unitsPerTexel);
  if (covered.length === 0) return [];

  const perRegion = new Map<RegionId, { count: number; best: CoveredTexel }>();
  for (const t of covered) {
    const seen = perRegion.get(t.region);
    if (!seen) perRegion.set(t.region, { count: 1, best: t });
    else {
      seen.count++;
      if (t.distance < seen.best.distance) seen.best = t;
    }
  }

  const total = covered.length;
  return [...perRegion.entries()]
    .map(([region, h]) => ({
      region,
      centre: { x: h.best.x, y: h.best.y },
      radius: radiusPx,
      weight: h.count / total,
    }))
    .sort((a, b) => b.weight - a.weight); // dominant face first
}
